import { useEffect, useRef, useState } from "react";

import type { FileItem } from "../api/client";
import { useKeydown } from "../hooks/useKeydown";

type Props = {
  items: FileItem[];
  index: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
};

const SWIPE_THRESHOLD = 60;

export function PreviewModal({ items, index, onClose, onIndexChange }: Props) {
  const file = items[index];
  const touchStartX = useRef<number | null>(null);
  const [error, setError] = useState(false);

  const next = () => onIndexChange(Math.min(index + 1, items.length - 1));
  const prev = () => onIndexChange(Math.max(index - 1, 0));

  useKeydown((e) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowLeft") prev();
    else if (e.key === "ArrowRight") next();
    else if (e.key.toLowerCase() === "d") triggerDownload(file);
  });

  useEffect(() => setError(false), [file?.id]);

  if (!file) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > SWIPE_THRESHOLD) prev();
    else if (dx < -SWIPE_THRESHOLD) next();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/95 text-white"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <header className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
        <span className="truncate">{file.original_name}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => triggerDownload(file)}
            className="rounded border border-white/30 px-3 py-1"
            aria-label="Lataa"
          >
            Lataa
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-white/30 px-3 py-1"
            aria-label="Sulje"
          >
            Sulje
          </button>
        </div>
      </header>
      <div className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
        <button
          type="button"
          onClick={prev}
          aria-label="Edellinen"
          disabled={index === 0}
          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-2xl disabled:opacity-30"
        >
          ‹
        </button>
        {error ? (
          <p className="text-sm text-white/70">Lataus epäonnistui.</p>
        ) : file.media_type === "image" ? (
          <img
            src={`/files/${file.id}.${extOf(file)}`}
            alt={file.original_name}
            className="max-h-full max-w-full object-contain"
            onError={() => setError(true)}
          />
        ) : (
          <video
            key={file.id}
            src={`/files/${file.id}.${extOf(file)}`}
            controls
            playsInline
            className="max-h-full max-w-full"
            onError={() => setError(true)}
          />
        )}
        <button
          type="button"
          onClick={next}
          aria-label="Seuraava"
          disabled={index === items.length - 1}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-2xl disabled:opacity-30"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function extOf(f: FileItem): string {
  const dot = f.original_name.lastIndexOf(".");
  return dot > 0 ? f.original_name.slice(dot + 1).toLowerCase() : f.media_type === "video" ? "mp4" : "jpg";
}

function triggerDownload(f: FileItem) {
  const a = document.createElement("a");
  a.href = `/files/${f.id}.${extOf(f)}?download=1`;
  a.download = f.original_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
